import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import { useApp } from "./AppContext";
import { useSalon } from "./SalonContext";
import { API_CONFIG, getApiUrl } from "../config/api";
import {
  GoogleAnalyticsRequest,
  GoogleAnalyticsResponse,
  RegisterCustomDomainRequest,
  RegisterCustomDomainResponse,
  DeployStatusResponse,
  ListAllDomainsResponse,
  RebuildWebsiteResponse,
  CheckSubdomainAvailabilityRequest,
  CheckSubdomainAvailabilityResponse,
  ClaimUpdateSubdomainRequest,
  ClaimUpdateSubdomainResponse,
  GetCurrentSubdomainResponse,
  GetDomainMappingsResponse,
  LoadPreviewDataResponse,
  PopulateSiteTemplateResponse,
  FieldSchema,
} from "../types/staticWebGenerator";

interface TemplateData {
  template_content: string;
  template_type: string;
  template_filename: string;
}

interface TemplateContextType {
  templateData: TemplateData | null;
  isLoading: boolean;
  error: string | null;
  draftSubdomain: string | null;
  refreshDraftSubdomain: () => Promise<void>;
  generatePreview: (salonId: string, previewData: any) => Promise<any>;
  generateManualPreview: (
    salonId: string,
    previewData: any,
  ) => Promise<PopulateSiteTemplateResponse>;
  fieldSchema: FieldSchema | null;
  addGoogleAnalytics: (
    salonId: string,
    request: GoogleAnalyticsRequest,
  ) => Promise<GoogleAnalyticsResponse>;
  // Custom Domain Management
  registerCustomDomain: (
    salonId: string,
    request: RegisterCustomDomainRequest,
  ) => Promise<RegisterCustomDomainResponse>;
  getDeployStatus: (
    salonId: string,
    customDomain?: string,
  ) => Promise<DeployStatusResponse>;
  listAllDomains: (salonId: string) => Promise<ListAllDomainsResponse>;
  // Website Rebuild
  rebuildWebsite: (salonId: string) => Promise<RebuildWebsiteResponse>;
  // Subdomain Management
  checkSubdomainAvailability: (
    request: CheckSubdomainAvailabilityRequest,
  ) => Promise<CheckSubdomainAvailabilityResponse>;
  claimUpdateSubdomain: (
    salonId: string,
    request: ClaimUpdateSubdomainRequest,
  ) => Promise<ClaimUpdateSubdomainResponse>;
  getCurrentSubdomain: (
    salonId: string,
    environment?: "live" | "draft",
  ) => Promise<GetCurrentSubdomainResponse>;
  // Domain Lookup API
  getDomainMappings: (apiKey: string) => Promise<GetDomainMappingsResponse>;
  // Preview Data
  loadPreviewData: (salonId: string) => Promise<LoadPreviewDataResponse>;
  clearError: () => void;
  clearTemplateData: () => void;
}

const TemplateContext = createContext<TemplateContextType | undefined>(
  undefined,
);

interface TemplateProviderProps {
  children: ReactNode;
}

export function TemplateProvider({ children }: TemplateProviderProps) {
  const [templateData, setTemplateData] = useState<TemplateData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftSubdomain, setDraftSubdomain] = useState<string | null>(null);
  const [fieldSchema, setFieldSchema] = useState<FieldSchema | null>(null);

  const { fetchWithAuth, getIdToken } = useApp();
  const salonCtx = useSalon() as any;
  const businessInfo = salonCtx?.businessInfo;

  const generatePreview = useCallback(
    async (salonId: string, previewData: any) => {
      try {
        setIsLoading(true);
        setError(null);

        const previewUrl = getApiUrl(
          API_CONFIG.ENDPOINTS.GENERATE_PREVIEW,
          "",
          { salonId },
        );

        const result = await fetchWithAuth(previewUrl, {
          method: "POST",
          body: JSON.stringify(previewData),
        });
        setIsLoading(false);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate preview";
        setError(errorMessage);
        console.error("Error generating preview:", err);
        setIsLoading(false);
        throw err;
      }
    },
    [fetchWithAuth],
  );

  const generateManualPreview = useCallback(
    async (
      salonId: string,
      previewData: any,
    ): Promise<PopulateSiteTemplateResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const populateSiteTemplateUrl = getApiUrl(
          API_CONFIG.ENDPOINTS.POPULATE_SITE_TEMPLATE,
          "",
          { salonId },
        );

        const result: PopulateSiteTemplateResponse = await fetchWithAuth(
          populateSiteTemplateUrl,
          {
            method: "POST",
            body: JSON.stringify(previewData),
          },
        );

        // Store the field schema for dynamic tab generation
        if (result.field_schema) {
          setFieldSchema(result.field_schema);
        }

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to generate manual preview";
        setError(errorMessage);
        console.error("Error generating manual preview:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchWithAuth],
  );

  const addGoogleAnalytics = useCallback(
    async (
      salonId: string,
      request: GoogleAnalyticsRequest,
    ): Promise<GoogleAnalyticsResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const apiUrl = getApiUrl(
          API_CONFIG.ENDPOINTS.ADD_GOOGLE_ANALYTICS,
          "",
          { salonId },
        );

        const response = await fetchWithAuth(apiUrl, {
          method: "POST",
          body: JSON.stringify(request),
        });

        setIsLoading(false);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to add Google Analytics";
        setError(errorMessage);
        console.error("Error adding Google Analytics:", err);
        setIsLoading(false);
        throw err;
      }
    },
    [fetchWithAuth],
  );

  // Custom Domain Management Methods
  const registerCustomDomain = useCallback(
    async (
      salonId: string,
      request: RegisterCustomDomainRequest,
    ): Promise<RegisterCustomDomainResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const apiUrl = getApiUrl(
          API_CONFIG.ENDPOINTS.REGISTER_CUSTOM_DOMAIN,
          "",
          { salonId },
        );

        const response = await fetchWithAuth(apiUrl, {
          method: "POST",
          body: JSON.stringify(request),
        });

        setIsLoading(false);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to register custom domain";
        setError(errorMessage);
        console.error("Error registering custom domain:", err);
        setIsLoading(false);
        throw err;
      }
    },
    [fetchWithAuth],
  );

  const getDeployStatus = useCallback(
    async (
      salonId: string,
      customDomain?: string,
    ): Promise<DeployStatusResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const queryParams = customDomain
          ? `custom_domain=${encodeURIComponent(customDomain)}`
          : "";
        const apiUrl = getApiUrl(
          API_CONFIG.ENDPOINTS.GET_DEPLOY_STATUS,
          queryParams,
          { salonId },
        );

        const response = await fetchWithAuth(apiUrl, {
          method: "GET",
        });

        setIsLoading(false);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to get deploy status";
        setError(errorMessage);
        console.error("Error getting deploy status:", err);
        setIsLoading(false);
        throw err;
      }
    },
    [fetchWithAuth],
  );

  const listAllDomains = useCallback(
    async (salonId: string): Promise<ListAllDomainsResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const apiUrl = getApiUrl(API_CONFIG.ENDPOINTS.LIST_ALL_DOMAINS, "", {
          salonId,
        });

        const response = await fetchWithAuth(apiUrl, {
          method: "GET",
        });

        setIsLoading(false);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to list domains";
        setError(errorMessage);
        console.error("Error listing domains:", err);
        setIsLoading(false);
        throw err;
      }
    },
    [fetchWithAuth],
  );

  // Website Rebuild Method
  const rebuildWebsite = useCallback(
    async (salonId: string): Promise<RebuildWebsiteResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const apiUrl = getApiUrl(API_CONFIG.ENDPOINTS.REBUILD_WEBSITE, "", {
          salonId,
        });

        const response = await fetchWithAuth(apiUrl, {
          method: "POST",
          body: JSON.stringify({}),
        });

        setIsLoading(false);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to rebuild website";
        setError(errorMessage);
        console.error("Error rebuilding website:", err);
        setIsLoading(false);
        throw err;
      }
    },
    [fetchWithAuth],
  );

  // Subdomain Management Methods
  const checkSubdomainAvailability = useCallback(
    async (
      request: CheckSubdomainAvailabilityRequest,
    ): Promise<CheckSubdomainAvailabilityResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const queryParams = `name=${encodeURIComponent(request.name)}${
          request.environment
            ? `&environment=${encodeURIComponent(request.environment)}`
            : ""
        }`;
        const apiUrl = getApiUrl(
          API_CONFIG.ENDPOINTS.CHECK_SUBDOMAIN_AVAILABILITY,
          queryParams,
          null,
        );

        const response = await fetchWithAuth(apiUrl, {
          method: "GET",
        });

        setIsLoading(false);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to check subdomain availability";
        setError(errorMessage);
        console.error("Error checking subdomain availability:", err);
        setIsLoading(false);
        throw err;
      }
    },
    [fetchWithAuth],
  );

  const claimUpdateSubdomain = useCallback(
    async (
      salonId: string,
      request: ClaimUpdateSubdomainRequest,
    ): Promise<ClaimUpdateSubdomainResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const apiUrl = getApiUrl(
          API_CONFIG.ENDPOINTS.CLAIM_UPDATE_SUBDOMAIN,
          "",
          { salonId },
        );

        const response = await fetchWithAuth(apiUrl, {
          method: "POST",
          body: JSON.stringify(request),
        });

        setIsLoading(false);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to claim/update subdomain";
        setError(errorMessage);
        console.error("Error claiming/updating subdomain:", err);
        setIsLoading(false);
        throw err;
      }
    },
    [fetchWithAuth],
  );

  const getCurrentSubdomain = useCallback(
    async (
      salonId: string,
      environment?: "live" | "draft",
    ): Promise<GetCurrentSubdomainResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const queryParams = environment
          ? `environment=${encodeURIComponent(environment)}`
          : "";
        const apiUrl = getApiUrl(
          API_CONFIG.ENDPOINTS.GET_CURRENT_SUBDOMAIN,
          queryParams,
          { salonId },
        );

        const response = await fetchWithAuth(apiUrl, {
          method: "GET",
        });

        setIsLoading(false);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to get current subdomain";
        setError(errorMessage);
        console.error("Error getting current subdomain:", err);
        setIsLoading(false);
        throw err;
      }
    },
    [fetchWithAuth],
  );

  // Function to fetch draft subdomain (reusable)
  const fetchDraftSubdomain = useCallback(async () => {
    if (!businessInfo?.id) return;

    try {
      // Try to get draft subdomain first
      const draftResponse = await getCurrentSubdomain(businessInfo.id, "draft");
      setDraftSubdomain(draftResponse.subdomain);
    } catch (err: any) {
      // If draft doesn't exist (404), try live as fallback
      if (err?.status === 404 || err?.response?.status === 404) {
        try {
          const liveResponse = await getCurrentSubdomain(
            businessInfo.id,
            "live",
          );
          setDraftSubdomain(liveResponse.subdomain);
        } catch (liveErr: any) {
          // If live also doesn't exist, set to null
          setDraftSubdomain(null);
        }
      } else {
        // For other errors, set to null
        setDraftSubdomain(null);
      }
    }
  }, [businessInfo?.id, getCurrentSubdomain]);

  // NOTE: Draft subdomain is fetched lazily when needed (not on mount)
  // Components that need draftSubdomain should call refreshDraftSubdomain()
  // This avoids unnecessary API calls on pages that don't use SSG features

  // Domain Lookup API Method
  const getDomainMappings = useCallback(
    async (apiKey: string): Promise<GetDomainMappingsResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const apiUrl = getApiUrl(
          API_CONFIG.ENDPOINTS.GET_DOMAIN_MAPPINGS,
          "",
          null,
        );

        // This endpoint uses API key authentication, not JWT
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "x-api-key": apiKey,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`,
          );
        }

        const data = await response.json();
        setIsLoading(false);
        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to get domain mappings";
        setError(errorMessage);
        console.error("Error getting domain mappings:", err);
        setIsLoading(false);
        throw err;
      }
    },
    [],
  );

  // Load Preview Data Method
  const loadPreviewData = useCallback(
    async (salonId: string): Promise<LoadPreviewDataResponse> => {
      try {
        setIsLoading(true);
        setError(null);

        const apiUrl = getApiUrl(API_CONFIG.ENDPOINTS.LOAD_PREVIEW_DATA, "", {
          salonId,
        });

        const response = await fetchWithAuth(apiUrl, {
          method: "GET",
        });

        // Store the field schema if it's included in the response
        if (response?.field_schema) {
          setFieldSchema(response.field_schema);
        }

        setIsLoading(false);
        // If no preview data exists, API returns empty object
        return response || {};
      } catch (err) {
        // Check if this is a 404 (no data exists) - this is normal for new websites
        const apiError = err as any;
        const status = apiError?.status || apiError?.response?.status;

        if (status === 404) {
          // 404 means no preview data exists yet - this is normal for new websites
          setIsLoading(false);
          return {}; // Return empty object to indicate no data exists
        }

        // For other errors, still return {} to allow wizard to continue
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load preview data";
        setError(errorMessage);
        console.error("Error loading preview data:", err);
        setIsLoading(false);
        // Return empty object on error to allow wizard to continue with empty state
        // The wizard can still work by generating a new preview
        return {};
      }
    },
    [fetchWithAuth],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearTemplateData = useCallback(() => {
    setTemplateData(null);
  }, []);

  // [LOOP] churn probe
  useValueChurnProbe("TemplateContext", { templateData, isLoading, error });

  // Memoized so consumers don't re-render on every provider render; every
  // method above is useCallback'd, so the deps are all stable references.
  const value: TemplateContextType = useMemo(
    () => ({
      templateData,
      isLoading,
      error,
      draftSubdomain,
      generatePreview,
      generateManualPreview,
      addGoogleAnalytics,
      registerCustomDomain,
      getDeployStatus,
      listAllDomains,
      rebuildWebsite,
      checkSubdomainAvailability,
      claimUpdateSubdomain,
      getCurrentSubdomain,
      getDomainMappings,
      loadPreviewData,
      clearError,
      clearTemplateData,
      refreshDraftSubdomain: fetchDraftSubdomain,
      fieldSchema,
    }),
    [
      templateData,
      isLoading,
      error,
      draftSubdomain,
      generatePreview,
      generateManualPreview,
      addGoogleAnalytics,
      registerCustomDomain,
      getDeployStatus,
      listAllDomains,
      rebuildWebsite,
      checkSubdomainAvailability,
      claimUpdateSubdomain,
      getCurrentSubdomain,
      getDomainMappings,
      loadPreviewData,
      clearError,
      clearTemplateData,
      fetchDraftSubdomain,
      fieldSchema,
    ],
  );

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
}

export function useTemplate() {
  const context = useContext(TemplateContext);
  if (context === undefined) {
    throw new Error("useTemplate must be used within a TemplateProvider");
  }
  return context;
}
